<template>
  <div class="fts-share-form">
    <!-- loading -->
    <fts-spinner show full-screen v-if="loading" />
    <!-- share form container-->
    <div class="share-form-container" v-else>
      <!-- content -->
      <div class="share-form-content">
        <div class="sf-content__header" v-if="formState !== 404 && formState !== 500">
          <div class="sf-company-container" v-if="headerLogo || headerName">
            <div class="sf-company-wrapper">
              <img :src="headerLogo" class="sf-company-logo" v-if="headerLogo" />
              <div class="sf-company-name">{{headerName}}</div>
            </div>
          </div>

          <div class="sf-content__header-title">
            <div
              class="sf-activity"
              v-if="activityId && activityId !== 'create' && activityId !== 'view'"
            >
              <!-- standard -->
              <template v-if="standardActivity">
                <!-- icon -->
                <i
                  :class="`sf-activity-icon ${standardActivity.icon}`"
                  v-if="standardActivity.icon"
                ></i>
                <!-- name -->
                <div class="sf-activity-name">{{standardActivity.name}}</div>
              </template>
              <!-- activity -->
              <template v-else>
                <!-- emoji -->
                <fts-moji
                  :emoji="header.activity.emoji"
                  :size="24"
                  class="sf-activity-emoji"
                  v-if="header.activity.emoji"
                />
                <!-- name -->
                <div class="sf-activity-name">{{header.activity.name}}</div>
              </template>
            </div>
          </div>

          <div class="sf-content__header-subtitle" v-if="header.title || header.state">
            <!-- title -->
            <div class="sf-title">{{header.title}}</div>
            <!-- state -->
            <fts-state :state="header.state" :states="states" tint class="sf-state" v-if="header.state && header.state.stateId" />
          </div>

          <div class="sf-content__header-entry">
            <div class="sf-content__header-entry-info">
              <!-- document id -->
              <div class="sf-document-id" v-if="header.documentId">{{header.documentId}}</div>
              <!-- module -->
              <div class="sf-module" v-if="module && module.name">
                <fts-moji
                  :emoji="module.emoji"
                  :size="16"
                  class="sf-module-emoji"
                  v-if="module.emoji"
                />
                <div class="sf-module-name">{{module && module.name}}</div>
              </div>
            </div>
            <!-- sync -->
            <fts-form-sync
              :synchronize="$refs.classification.synchronize"
              v-if="isFormSync && $refs.classification"
            />
          </div>
        </div>

        <div class="sf-form-state-container" v-if="formState === 200">
          <div class="sf-form-state-icon sf-form-state-icon--success">
            <font-awesome-icon :icon="['far', 'check-circle']" />
          </div>
          <div class="sf-form-state-label">Success</div>
          <div class="sf-form-state-description">
            <span v-if="message">{{message}}</span>
            <span v-else>
              Your form has been submitted.
              <br />If you have any questions, just contact the person who shared it with you.
            </span>
          </div>
        </div>

        <div class="sf-form-state-container" v-else-if="formState === 404">
          <div class="sf-form-state-icon sf-form-state-icon--error">
            <font-awesome-icon :icon="['far', 'times-circle']" />
          </div>
          <div class="sf-form-state-label">Form not found</div>
          <div class="sf-form-state-description" v-if="error">{{error}}</div>
        </div>

        <div class="sf-form-state-container" v-else-if="formState === 400 || formState === 500">
          <div class="sf-form-state-icon sf-form-state-icon--error">
            <font-awesome-icon :icon="['far', 'times-circle']" />
          </div>
          <div class="sf-form-state-label">Something went wrong</div>
          <div class="sf-form-state-description" v-if="error">{{error}}</div>
          <fts-btn outline text-transform-init class="sf-form-state-action" @click="retry">Try again</fts-btn>
        </div>

        <div class="sf-form-container" v-else>
          <fts-classification
            :key="activityId"
            :locus-id="header.id"
            :vector-id="header.moduleId"
            :activity-id="activityId"
            :activity="activityName"
            :users="users"
            comfort-design
            :view="activityId == 'view'"
            share-form
            form-element-overflow-auto
            :form-validation-error="formValidationError"
            :comment="comment"
            :comment-mention="false"
            :comment-section="isCommentRight && !disableComment"
            :submitting.sync="submitting"
            :synchronizing.sync="synchronizing"
            @submit="submit"
            ref="classification"
            v-if="activityId"
          />

          <fts-btn
            color="primary"
            text-transform-init
            box-shadow-none
            lg
            :disabled="synchronizing"
            :loading="submitting"
            class="sf-form-action"
            @click="$refs.classification && $refs.classification.submit()"
            v-if="activityId != 'view'"
          >Submit</fts-btn>
        </div>
      </div>
      <!-- brand (same footer as the Inistate emails, templates/emails/master.html) -->
      <div class="share-form-brand">
        <a :href="websiteURL" target="_blank" rel="noopener" class="sf-brand-logo">
          <img src="./assets/images/logo/inistate-logo-full-transparent.png" alt="Inistate" />
        </a>

        <div class="sf-brand-description">
          <a :href="websiteURL" target="_blank" rel="noopener" class="sf-brand-name">Inistate</a> &mdash; let everyone have their own app.
          <br />Get your own app and make lasting positive changes for your business.
        </div>

        <div class="sf-brand-legal">
          <a :href="termsURL" target="_blank" rel="noopener" class="sf-brand-legal-link">Terms</a>
          <span class="sf-brand-legal-separator">&middot;</span>
          <a :href="privacyPolicyURL" target="_blank" rel="noopener" class="sf-brand-legal-link">Privacy Policy</a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { classificationStore } from './components/store/classificationStore';
import { clientStore } from './components/store/clientStore';
import webAppConfig from './components/commons/webApp.config';
import commons from './components/commons/commons';
import { applyDisplay } from './components/commons/readability';
import LocalStorageService from './components/store/LocalStorageService';
import confetti from './components/commons/confetti';

import FTSBtn from './components/elements/FTSBtn';
import FTSClassification from './components/elements/FTSClassification';
import FTSFormSync from './components/elements/form/FTSFormSync';
import FTSMoji from './components/elements/FTSMoji';
import FTSSpinner from './components/elements/FTSSpinner';
import FTSState from './components/elements/FTSState';
import FTSTippy from './components/elements/FTSTippy';

import _ from "lodash";

export default {
  metaInfo() {
    return {
      title: this.metaTitle
    };
  },
  components: {
    "fts-btn": FTSBtn,
    "fts-classification": FTSClassification,
    "fts-form-sync": FTSFormSync,
    "fts-moji": FTSMoji,
    "fts-spinner": FTSSpinner,
    "fts-state": FTSState,
    "fts-tippy": FTSTippy
  },
  data: () => ({
    disableComment: false,
    loading: false,
    submitting: false,
    synchronizing: false,
    formState: null,
    isFormSync: false,
    message: null,
    error: null,
    comment: {},
    header: {
      module: {},
      activity: {}
    }
  }),
  methods: {
    initialClassificationPollData() {
      this.$refs.classification.pollData({
        id: this.activityId,
        name: this.activityName
      });
    },
    async loadForm() {
      this.disableComment = this.$route.query.comment == 'no';
      this.loading = true;
      this.formState = null;
      if (this.$route.params.id) {
        try {
          await clientStore.dispatch("loadWorkspaceShareForm", {
            route: this.$route
          });
          let result = await classificationStore.dispatch("loadActivityForm", {
            route: this.$route,
            users: []
          });
          this.loading = false;
          this.header = result.header;
          this.isFormSync = result.needSync;

          if (this.header.id) {
            classificationStore.dispatch("setLocusId", this.header.id);
          }

          if (this.isFormSync) {
            this.$nextTick(() => {
              this.initialClassificationPollData();
            });
          }

          if (result.style) {
            const styleEl = document.createElement('style');
            styleEl.textContent = result.style;
            document.head.appendChild(styleEl);
          }
        } catch (err) {
          this.formState = 500;
          this.error =
            "We couldn't open this form. Please try again, or ask the person who shared it for a new link.";
          if (err.response && err.response.data && err.response.data.message) {
            this.error = err.response.data.message;
          }
        }
      } else {
        this.formState = 404;
        this.error = "Make sure you opened the full link, or ask the person who shared it for a new one.";
      }
      this.loading = false;
    },
    formValidationError() {
      this.$nextTick(() => {
        const el = document.getElementsByClassName(
          "fts-form-group form-group--error"
        )[0];
        el && el.scrollIntoView();
      });
    },
    async submit(formData) {
      this.submitting = true;
      try {
        let result = await classificationStore.dispatch("submitActivity", {
          moduleId: this.header.moduleId,
          activityId: this.activityId,
          entryIds: this.header.id ? [this.header.id] : null,
          payload: formData,
          post: this.comment
        });
        this.submitting = false;
        this.formState = 200;
        this.message = result.data.message;

        if (result.data.result && result.data.result.title) {
          this.header.title = result.data.result.title;
        }

        if (result.data.celebrate) {
          confetti();
        }
        
        if (window.self !== window.top) {
          window.parent.postMessage(JSON.stringify({ 
            type: "inistate",
            entryId: result.data.entryId
          }), "*");
        }
      } catch (err) {
        this.error = "We couldn't submit the form. Please try again.";
        if (err.response && err.response.data && err.response.data.message) {
          this.error = err.response.data.message;
        }
        this.submitting = false;
        this.formState = 400;
      }
    },
    retry() {
      if (this.formState === 400) {
        this.formState = null;
        if (this.isFormSync) {
          this.$nextTick(() => {
            this.initialClassificationPollData();
          });
        }
      } else {
        this.loadForm();
      }
    }
  },
  computed: {
    metaTitle() {
      if (this.headerName && this.module && this.module.name) {
        return commons.externalMetaTitle({
          documentId: this.header && this.header.documentId,
          activityId: this.standardActivity
            ? this.standardActivity.id
            : this.activityId,
          activityName: this.standardActivity
            ? this.standardActivity.name
            : this.activityName,
          moduleName: this.module.name,
          workspaceName: this.headerName
        });
      }
    },
    headerName() {
      return (
        clientStore.getters.companyName || clientStore.getters.workspaceName
      );
    },
    headerLogo() {
      const filePath = clientStore.getters.companyLogo;

      if (filePath) {
        const url = filePath.split('/');
        const filename = encodeURIComponent(url.pop());
        const formattedUrl = `${url.join("/")}/${filename}`;
        return `${webAppConfig.baseURL}/api/d/${clientStore.getters.workspaceId}/s${formattedUrl}`;
      }
    },
    module() {
      if (this.header && this.header.moduleId) {
        return clientStore.state.workspaceModules.find(
          x => x.id === this.header.moduleId
        );
      }
    },
    standardActivity() {
      return commons.standardActivities.find(x => x.id === this.activityId);
    },
    activityId() {
      if (this.header && this.header.activity && this.header.activity.id) {
        return this.header.activity.id;
      }
      return this.header.standard;
    },
    activityName() {
      if (this.header && this.header.activity && this.header.activity.name) {
        return this.header.activity.name;
      } else if (this.header && this.header.id) {
        return "edit";
      }
      return "create";
    },
    states() {
      return clientStore.getters.workspaceStates;
    },
    users() {
      return clientStore.getters.workspaceUsers || [];
    },
    isCommentRight() {
      return !Boolean(this.$route.query.comment && this.$route.query.comment == '0');
    }
  },
  watch: {
    $route(to, from) {
      this.loadForm();
    }
  },
  beforeMount() {
    document.body.classList.add("fts-scrollbar");
  },
  created() {
    this.websiteURL = commons.websiteURL;
    this.termsURL = commons.termsURL;
    this.privacyPolicyURL = commons.privacyPolicyURL;
  },
  mounted() {
    // reading preference of whoever is signed in on this browser; a visitor has none, so
    // the form shows Default (FTR00375)
    applyDisplay(LocalStorageService.getUserName());
    this.loadForm();
  }
};
</script>

<style lang="scss">
@use "./src/assets/scss/vars" as *;

.fts-share-form {
  &::before {
    content: "";
    position: fixed;
    height: 100%;
    width: 100%;
    background-color: var(--fts-background);
    z-index: -1;
  }
  .share-form-container {
    padding: 0.75rem 0.75rem 2rem;
  }
  .share-form-content {
    max-width: 642px;
    margin-left: auto;
    margin-right: auto;
    border: 1px solid var(--fts-border);
    border-radius: 0.75rem;
    background-color: var(--fts-card-background);
    color: var(--fts-text);
    .sf-content__header {
      padding: 1.5rem 1rem 0;
      .sf-company-container {
        margin-bottom: 1.5rem;
        padding-top: 0.5rem;
        padding-bottom: 1.5rem;
        border-bottom: 1px solid var(--fts-gray-100);
        .sf-company-wrapper {
          display: flex;
          align-items: center;
          justify-content: center;
          margin-left: 1rem;
          margin-right: 1rem;
        }
        .sf-company-logo {
          max-height: 2.25rem;
        }
        .sf-company-name {
          margin-left: 0.5rem;
          font-weight: 600;
          font-size: 1.25rem;
          line-height: 1.75rem;
          color: var(--fts-gray-900);
        }
      }
      .sf-content__header-title {
        margin-bottom: 0.5rem;
      }
      .sf-content__header-subtitle {
        display: flex;
        align-items: center;
        margin-bottom: 0.25rem;
      }
      .sf-content__header-entry {
        display: flex;
        align-items: flex-start;

        .sf-content__header-entry-info {
          @include flex-gap(
            $display: flex,
            $gapLeft: 0.75rem,
            $gapTop: 0.375rem
          );
          flex: 1;
          min-width: 0;
          font-size: 0.875rem;
          line-height: 1.25rem;
          color: var(--fts-gray-600);
        }
      }
      .sf-activity {
        flex: 1;
        display: flex;
        align-items: center;
        .sf-activity-emoji {
          margin-right: 0.375rem;
        }
        .sf-activity-icon {
          margin-right: 0.375rem;
          font-size: 1.25rem;
          color: var(--fts-default);
        }
        .sf-activity-name {
          font-weight: 700;
          font-size: 1.25rem;
          line-height: 1.75rem;
          color: var(--fts-gray-900);
        }
      }
      .sf-title {
        flex: 1;
        font-size: 1.125rem;
        line-height: 1.5rem;
        color: var(--fts-gray-700);
      }
      .sf-state {
        margin-left: 0.375rem;
        > div {
          font-size: 0.875rem;
          line-height: 1.5rem;
        }
      }
      .sf-document-id {
        @include text-overflow-ellipsis;
      }
      .sf-module {
        min-width: 0;
        display: flex;
        align-items: center;
        .sf-module-emoji {
          margin-right: 0.375rem;
        }
        .sf-module-name {
          @include text-overflow-ellipsis;
        }
      }
    }
    .sf-form-state-container {
      padding: 3rem 1.5rem;
      text-align: center;
      .sf-form-state-icon {
        margin-bottom: 1rem;
        font-size: 3.5rem;
        line-height: 1;
        &.sf-form-state-icon--success {
          color: var(--fts-success);
        }
        &.sf-form-state-icon--error {
          color: var(--fts-danger);
        }
      }
      .sf-form-state-label {
        font-weight: 700;
        font-size: 1.5rem;
        line-height: 2rem;
        color: var(--fts-gray-900);
      }
      .sf-form-state-description {
        margin-top: 0.5rem;
        padding-left: 1rem;
        padding-right: 1rem;
        font-size: 1rem;
        line-height: 1.5rem;
        color: var(--fts-gray-700);
      }
      .sf-form-state-action.fts-btn {
        margin-top: 1.5rem;
        border-radius: 0.375rem;
        letter-spacing: 0;
      }
    }
    .sf-form-container {
      padding: 2rem 1rem 1.5rem;
    }
    /* the email CTA: 48px, no tracking; full width on phones */
    .sf-form-action.fts-btn {
      width: 100%;
      min-height: 3rem;
      margin-top: 2rem;
      padding-left: 2rem;
      padding-right: 2rem;
      border-radius: 0.375rem;
      font-weight: 600;
      letter-spacing: 0;
    }
  }
  .share-form-brand {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 2rem;
    padding-left: 1rem;
    padding-right: 1rem;
    font-size: 0.75rem;
    line-height: 1.125rem;
    color: var(--fts-gray-600);
    text-align: center;
    .sf-brand-logo {
      > img {
        display: block;
        height: 1.75rem;
      }
    }
    .sf-brand-description {
      margin-top: 0.5rem;
      .sf-brand-name {
        font-weight: 700;
        color: var(--fts-primary);
        text-decoration: none;
      }
    }
    .sf-brand-legal {
      margin-top: 0.75rem;
      user-select: none;
      .sf-brand-legal-link {
        color: inherit;
        text-decoration: underline;
        &:hover {
          color: var(--fts-gray-800);
        }
      }
      .sf-brand-legal-separator {
        margin-left: 0.5rem;
        margin-right: 0.5rem;
      }
    }
  }
  /* to-do (temp) */
  .fts-classification {
    .fts-form-container {
      .fts-form-wrapper {
        margin-left: 0;
        margin-right: 0;
        .fts-form-element {
          padding-left: 0;
          padding-right: 0;
        }
        .fts-form-section {
          min-width: 100%;
          width: 100%;
          flex: 1 1 100%;
          margin-left: 0;
          margin-right: 0;
          .fts-form-container {
            > .form-row {
              min-width: 250px;
            }
          }
        }
      }
    }
    .fts-form-label {
      scroll-margin: 1rem;
    }
  }
  /* media queries */
  @media (min-width: 640px) {
    .share-form-container {
      padding-top: 2rem;
      padding-bottom: 2.5rem;
    }
    .share-form-content {
      .sf-content__header {
        padding-left: 1.5rem;
        padding-right: 1.5rem;
        .sf-company-container {
          .sf-company-name {
            font-size: 1.5rem;
            line-height: 2rem;
          }
        }
        .sf-activity {
          .sf-activity-icon {
            font-size: 1.5rem;
          }
          .sf-activity-name {
            font-size: 1.5rem;
            line-height: 2rem;
          }
        }
        .sf-title {
          font-size: 1.25rem;
          line-height: 1.75rem;
        }
      }
      .sf-form-container {
        padding-left: 1.5rem;
        padding-right: 1.5rem;
      }
      .sf-form-action.fts-btn {
        width: auto;
        min-width: 10rem;
      }
    }
  }
}
</style>